// angular
import { Injectable } from '@angular/core';

// addons
import { Observable } from 'rxjs';

// services
import { ApiService } from './api.service';
import { CacheService } from './cache.service';

// models
import {
  IOrganization,
  IOrganizationCreate,
  IOrganizationEdit,
  IOrganizationSubmit,
  ITrade,
  ITradeSlim,
  IUser,
  OrganizationStatusEnum
} from '@shared/models';

@Injectable()
export class OrganizationService {
  constructor(
    private apiService: ApiService,
    private cacheService: CacheService
  ) {}

  initiateOrganization(organization: IOrganizationSubmit): Observable<void> {
    return this.apiService.post('/organizations/initiate', organization);
  }

  saveDraftInitiationOrganization(organization: IOrganizationSubmit): Observable<void> {
    return this.apiService.post('/organizations/pre-initiate', organization);
  }

  getDraftInitiationOrganization(): Observable<IOrganizationSubmit> {
    return this.apiService.get('/organizations/pre-initiate');
  }

  createOrganization(organization: IOrganizationCreate): Observable<void> {
    return this.apiService.post('/organizations', organization);
  }

  updateOrganization(organization: IOrganizationEdit): Observable<void> {
    return this.apiService.put2(`/organizations/${organization.id}`, organization);
  }

  setOrganizationStatusToCache(status: OrganizationStatusEnum): Observable<IUser> {
    return this.cacheService.setToCache({ key: 'organizationStatus', value: status });
  }

  getOrganizations(filter): Observable<IOrganization[]> {
    const queryParams = this.apiService.mapParams(filter);

    return this.apiService.get('/organizations', queryParams);
  }

  getOrganization(id: number): Observable<IOrganization> {
    return this.apiService.get2(`/organizations/${id}`);
  }

  declineOrganization(id: number, comment: { comment: string }): Observable<void> {
    return this.apiService.put(`/organizations/${id}/decline`, comment);
  }

  approveOrganization(id: number): Observable<void> {
    return this.apiService.put(`/organizations/${id}/approve`);
  }

  getTrades(id: number): Observable<ITradeSlim[]> {
    return this.apiService.get(`/organizations/${id}/trades`);
  }

  getTrade(id: number, tradeId: number): Observable<ITrade> {
    return this.apiService.get(`/organizations/${id}/trades/${tradeId}`);
  }

  removeOrganization(id: number): Observable<void> {
    return this.apiService.delete2(`/organizations/${id}`);
  }

  enableMfa(id: number): Observable<void> {
    return this.apiService.post2(`/organizations/${id}/mfa/enable`);
  }

  disableMfa(id: number): Observable<void> {
    return this.apiService.post2(`/organizations/${id}/mfa/disable`);
  }

  enableMfaTestMode(id: number): Observable<void> {
    return this.apiService.post2(`/organizations/${id}/mfa/email/enable`);
  }

  disableMfaTestMode(id: number): Observable<void> {
    return this.apiService.post2(`/organizations/${id}/mfa/email/disable`);
  }

}
